import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaCalendar, FaCamera, FaHeart, FaEye, FaImages } from 'react-icons/fa';
import { getImageUrl, getPublicEvents } from '../services/api';

function PublicEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState('');

  // Only re-fetch when the applied search term, sort, or page changes —
  // not on every keystroke in the input.
  useEffect(() => {
    fetchEvents();
  }, [appliedSearch, sortBy, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getPublicEvents({
        search: appliedSearch,
        sort_by: sortBy,
        page,
        per_page: 12,
      });
      // api interceptor returns response.data directly, so fields are top-level
      setEvents(response.events || []);
      setPagination(response.pagination || null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Impossible de charger les événements. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Setting appliedSearch + page together; useEffect will run once both settle
    setAppliedSearch(searchQuery);
    setPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setPage(1);
  };

  return (
    <div className="container" style={{ padding: '2rem 0', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ marginBottom: '1rem', fontSize: '3rem', color: '#424242' }}>
          Découvrir les événements
        </h1>
        <p style={{ fontSize: '1.2rem', color: '#757575' }}>
          Parcourez les événements publics et retrouvez vos photos
        </p>
      </div>

      {/* Search & Filters */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '15px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        marginBottom: '2rem'
      }}>
        <form onSubmit={handleSearch} style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '1rem',
          alignItems: 'center'
        }}>
          {/* Search Input */}
          <div style={{ position: 'relative' }}>
            <FaSearch style={{
              position: 'absolute', left: '1rem', top: '50%',
              transform: 'translateY(-50%)', color: '#757575', fontSize: '1.2rem'
            }} />
            <input
              type="text"
              placeholder="Rechercher par nom, lieu ou catégorie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '1rem 1rem 1rem 3rem',
                borderRadius: '10px', border: '2px solid #e0e0e0',
                fontSize: '1rem', transition: 'border-color 0.3s', boxSizing: 'border-box',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#FF8C42'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Sort Select */}
          <select
            value={sortBy}
            onChange={handleSortChange}
            style={{
              padding: '1rem', borderRadius: '10px',
              border: '2px solid #e0e0e0', fontSize: '1rem', cursor: 'pointer'
            }}
          >
            <option value="created_at">Plus récents</option>
            <option value="name">Nom (A-Z)</option>
            <option value="date">Date de l'événement</option>
          </select>

          {/* Search Button */}
          <button
            type="submit"
            style={{
              padding: '1rem 2rem', borderRadius: '10px', border: 'none',
              background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B35 100%)',
              color: 'white', fontSize: '1rem', fontWeight: '600',
              cursor: 'pointer', whiteSpace: 'nowrap'
            }}
          >
            Rechercher
          </button>
        </form>

        {/* Results info */}
        {!loading && pagination && (
          <div style={{ marginTop: '1rem', color: '#757575', fontSize: '0.95rem' }}>
            {pagination.total} événement{pagination.total !== 1 ? 's' : ''} trouvé{pagination.total !== 1 ? 's' : ''}
            {appliedSearch && ` correspondant à "${appliedSearch}"`}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="error-message" style={{ marginBottom: '1.5rem' }}>{error}</div>
      )}

      {/* Events Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#757575' }}>Chargement des événements...</p>
        </div>
      ) : events.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '4rem', background: 'white',
          borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          <FaImages style={{ fontSize: '4rem', color: '#e0e0e0', marginBottom: '1rem' }} />
          <h3 style={{ color: '#424242', marginBottom: '0.5rem' }}>Aucun événement trouvé</h3>
          <p style={{ color: '#757575' }}>
            {appliedSearch ? 'Essayez un autre terme de recherche' : "Aucun événement public n'est disponible pour le moment"}
          </p>
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '2rem'
          }}>
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => navigate(`/event/${event.id}/browse`)}
                style={{
                  background: 'white', borderRadius: '15px', overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  cursor: 'pointer', transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
                }}
              >
                {/* Cover */}
                <div style={{
                  width: '100%', height: '200px', position: 'relative',
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B35 100%)',
                }}>
                  {event.cover_photo ? (
                    <img
                      src={getImageUrl(event.cover_photo)}
                      alt={`Couverture de ${event.name}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '4rem', opacity: 0.5
                    }}>📸</div>
                  )}

                  {/* Category Badge */}
                  <div style={{
                    position: 'absolute', top: '1rem', right: '1rem',
                    background: 'rgba(255,255,255,0.9)', padding: '0.5rem 1rem',
                    borderRadius: '20px', fontSize: '0.85rem',
                    fontWeight: '600', color: '#424242'
                  }}>
                    {event.category}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{
                    margin: '0 0 0.8rem 0', color: '#424242',
                    fontSize: '1.3rem', fontWeight: '600'
                  }}>
                    {event.name}
                  </h3>

                  {event.description && (
                    <p style={{
                      margin: '0 0 1rem 0', color: '#757575', fontSize: '0.95rem',
                      lineHeight: '1.5', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                    }}>
                      {event.description}
                    </p>
                  )}

                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: '0.5rem',
                    marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #f5f5f5'
                  }}>
                    {event.location && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#757575', fontSize: '0.9rem' }}>
                        <FaMapMarkerAlt style={{ color: '#FF8C42' }} />
                        {event.location}
                      </div>
                    )}
                    {event.event_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#757575', fontSize: '0.9rem' }}>
                        <FaCalendar style={{ color: '#4CAF50' }} />
                        {new Date(event.event_date).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        })}
                      </div>
                    )}
                    {event.photographer?.name && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/photographer/${event.photographer.id}`);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#757575',
                          fontSize: '0.9rem',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <FaCamera style={{ color: '#9C27B0' }} />
                        {event.photographer.name}
                      </button>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#757575' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FaImages /> {event.photo_count ?? 0}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FaHeart style={{ color: '#FF6B35' }} /> {event.likes_count ?? 0}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FaEye /> {event.views_count ?? 0}
                      </span>
                    </div>
                    <button
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
                        background: '#FF8C42', color: 'white',
                        fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer'
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/event/${event.id}/browse`); }}
                    >
                      Voir l'événement
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div style={{
              marginTop: '3rem', display: 'flex',
              justifyContent: 'center', gap: '0.5rem'
            }}>
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={!pagination.has_prev}
                style={{
                  padding: '0.8rem 1.5rem', borderRadius: '8px', border: 'none',
                  background: pagination.has_prev ? 'white' : '#f5f5f5',
                  color: pagination.has_prev ? '#424242' : '#9e9e9e',
                  cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}
              >
                Précédent
              </button>

              {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    style={{
                      padding: '0.8rem 1.2rem', borderRadius: '8px', border: 'none',
                      background: page === pageNum ? '#FF8C42' : 'white',
                      color: page === pageNum ? 'white' : '#424242',
                      cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                      fontWeight: page === pageNum ? '600' : 'normal'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.has_next}
                style={{
                  padding: '0.8rem 1.5rem', borderRadius: '8px', border: 'none',
                  background: pagination.has_next ? 'white' : '#f5f5f5',
                  color: pagination.has_next ? '#424242' : '#9e9e9e',
                  cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PublicEventsPage;
